"use client"

import * as React from "react"
import { Cross2Icon } from "@radix-ui/react-icons"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { 
  Loader2,
  CheckCircle2,
  AlertOctagon,
  AlertCircle,
  Bell
} from "lucide-react"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-xl border p-6 pr-10 shadow-2xl backdrop-blur-md transition-all duration-300 ease-out data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full [&>div>h3]:text-lg [&>div>h3]:font-bold [&>div>p]:text-sm [&>div>p]:font-medium [&>div>div]:text-sm [&>div>div]:font-medium",
  {
    variants: {
      variant: {
        default:
          "border-gray-200/50 bg-white/95 text-gray-900 shadow-gray-200/50 dark:border-gray-700/50 dark:bg-gray-900/95 dark:text-gray-50 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-gradient-to-b before:from-gray-400 before:to-gray-600 before:transition-all before:duration-300 group-hover:before:w-2 dark:before:from-gray-500 dark:before:to-gray-400",

        destructive:
          "border-red-200/50 bg-red-50/95 text-red-900 shadow-red-200/50 dark:border-red-800/50 dark:bg-red-950/95 dark:text-red-50 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-gradient-to-b before:from-red-500 before:to-red-700 before:transition-all before:duration-300 group-hover:before:w-2",

        success:
          "border-emerald-200/50 bg-emerald-50/95 text-emerald-900 shadow-emerald-200/50 dark:border-emerald-800/50 dark:bg-emerald-950/95 dark:text-emerald-50 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-gradient-to-b before:from-emerald-500 before:to-emerald-700 before:transition-all before:duration-300 group-hover:before:w-2",

        warning:
          "border-amber-200/50 bg-amber-50/95 text-amber-900 shadow-amber-200/50 dark:border-amber-800/50 dark:bg-amber-950/95 dark:text-amber-50 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-gradient-to-b before:from-amber-500 before:to-amber-700 before:transition-all before:duration-300 group-hover:before:w-2",

        info:
          "border-slate-200/50 bg-slate-50/95 text-slate-900 shadow-slate-200/50 dark:border-slate-800/50 dark:bg-slate-950/95 dark:text-slate-50 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-gradient-to-b before:from-slate-500 before:to-slate-700 before:transition-all before:duration-300 group-hover:before:w-2",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)


const ToastIcon = React.forwardRef<
  HTMLDivElement,
  { variant?: VariantProps<typeof toastVariants>["variant"] }
>(({ variant }, ref) => {
  const icons = {
    default: (
      <div className="relative">
        <Bell className="h-5 w-5 animate-spin animate-duration-3000" />
        <div className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-foreground/20 animate-ping" />
      </div>
    ),
    destructive: (
      <div className="relative">
        <AlertOctagon className="h-5 w-5 animate-pulse" />
        <div className="absolute -inset-1 rounded-full bg-red-500/20 animate-pulse" />
      </div>
    ),
    success: (
      <div className="relative">
        <CheckCircle2 className="h-5 w-5 animate-bounce animate-duration-1000" />
        <div className="absolute -inset-1 rounded-full bg-green-500/20 animate-pulse" />
      </div>
    ),
    warning: (
      <div className="relative">
        <AlertCircle className="h-5 w-5 animate-pulse" />
        <div className="absolute -inset-1 rounded-full bg-yellow-500/20 animate-pulse" />
      </div>
    ),
    info: (
      <div className="relative">
        <Loader2 className="h-5 w-5 animate-spin" />
        <div className="absolute -inset-1 rounded-full bg-slate-500/20 animate-pulse" />
      </div>
    )
  }

  return (
    <div 
      ref={ref} 
      className="mr-3 transition-transform duration-200 ease-in-out group-hover:scale-110"
    >
      {variant ? icons[variant] : icons.default}
    </div>
  )
})
ToastIcon.displayName = "ToastIcon"

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, children, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    >
      <ToastIcon variant={variant} />
      <div className="flex-1 transition-transform duration-200 ease-in-out group-hover:translate-x-1">
        {children}
      </div>
    </ToastPrimitives.Root>
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center justify-center rounded-md border bg-transparent px-4 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-red-500/30 group-[.destructive]:hover:border-red-500/30 group-[.destructive]:hover:bg-red-500 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-500",
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    )}
    toast-close=""
    {...props}
  >
    <Cross2Icon className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-90 leading-relaxed", className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>
type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}